import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { servicesAPI, fleetAPI, companyAPI } from '../utils/api';
import { MapPin, Briefcase, Plane, Clock, Navigation, Building, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useNavigate } from 'react-router-dom';
import Seo from '../components/Seo';

const iconMap = { MapPin, Briefcase, Plane, Clock, Navigation, Building };

const ServicesPage = () => {
  const [services, setServices] = useState([]);
  const [fleet, setFleet] = useState([]);
  const [companyInfo, setCompanyInfo] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    try {
      const [servicesRes, fleetRes, companyRes] = await Promise.all([
        servicesAPI.getAll(),
        fleetAPI.getAll(),
        companyAPI.getInfo(),
      ]);
      setServices(servicesRes.data);
      setFleet(fleetRes.data);
      setCompanyInfo(companyRes.data);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Seo
        pageKey="services"
        seo={companyInfo?.seo}
        defaults={{
          title: 'Our Services | Employee Transport, Intercity & Airport Cabs – Ecoway Cabs',
          description: 'Explore Ecoway Cabs services: corporate employee transportation, intercity travel, airport & railway transfers, and business cab solutions with a modern, well-maintained fleet.',
          keywords: 'employee transportation, intercity cab service, airport transfer, corporate cabs, railway transfer, fleet services India',
        }}
        path="/services"
      />
      <section className="bg-gradient-to-r from-blue-900 to-indigo-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-5xl font-bold mb-4">Our Services</h1>
          <p className="text-xl text-blue-100">{companyInfo?.services_subtitle || 'Comprehensive transportation solutions tailored to your needs'}</p>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {services.map((service) => {
              const Icon = iconMap[service.icon] || MapPin;
              return (
                <Card key={service.id} className="hover:shadow-xl transition-shadow">
                  <CardHeader>
                    <div className="flex items-start">
                      <div className="bg-blue-100 w-16 h-16 rounded-lg flex items-center justify-center mr-4">
                        <Icon className="h-8 w-8 text-blue-600" />
                      </div>
                      <div>
                        <CardTitle className="text-2xl">{service.title}</CardTitle>
                        <p className="text-gray-600 mt-2">{service.description}</p>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <h4 className="font-semibold mb-3">Key Features:</h4>
                    <ul className="space-y-2">
                      {service.features.map((feature) => (
                        <li key={feature} className="flex items-center">
                          <CheckCircle className="h-5 w-5 text-green-500 mr-3" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Fleet</h2>
            <p className="text-xl text-gray-600">Wide range of vehicles to meet your requirements</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {fleet.map((vehicle) => (
              <Card key={vehicle.id}>
                <CardContent className="p-0">
                  <img src={vehicle.image_url} alt={vehicle.name} className="w-full h-48 object-cover rounded-t-lg" />
                  <div className="p-4">
                    <h3 className="font-semibold text-lg mb-1">{vehicle.name}</h3>
                    <p className="text-sm text-gray-600 mb-2">{vehicle.category}</p>
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm text-gray-600">Seating: {vehicle.seating_capacity}</span>
                      <span className="text-blue-600 font-bold">₹{vehicle.price_per_km}/km</span>
                    </div>
                    <div className="space-y-1">
                      {vehicle.features.slice(0, 3).map((feature) => (
                        <div key={feature} className="text-xs text-gray-600 flex items-center">
                          <CheckCircle className="h-3 w-3 text-green-500 mr-2" />
                          {feature}
                        </div>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-gradient-to-r from-blue-900 to-indigo-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">Need a Customized Solution?</h2>
          <p className="text-lg text-blue-100 mb-6">Contact us for tailored transportation services</p>
          <Button size="lg" className="bg-white text-blue-900 hover:bg-gray-100" onClick={() => navigate('/quote')}>
            Request a Quote
          </Button>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
